import React, { useEffect, useCallback } from 'react';

import _ from 'lodash';

import { useSelector, useDispatch } from 'react-redux';

import QrCode from 'qrcode.react';

import { Button, Spin, Box } from '@kufox/mui';

import { styled } from '@kufox/mui/emotion';

import { fade } from '@kufox/mui/utils/colorManipulator';

import {
  SCANLOGINTABKEY,
  SCAN_AUTHORIZED,
  SCAN_EXPIRED,
  SCAN_CANCELED,
  SCAN_SCANNED,
  NAMESPACE,
} from '../constants';

import { useLang } from '../../hookTool';

import info from '../../../static/info.svg';

const QrBox = styled.div`
  width: 244px;
  height: 244px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.cover4};
  background: ${({ theme }) => theme.colors.cover4};
`;

const Corner = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  &[data-corner='1'] {
    top: -1px;
    left: -1px;
    border-bottom: none;
    border-right: none;
  }
  &[data-corner='2'] {
    top: -1px;
    right: -1px;
    border-bottom: none;
    border-left: none;
  }
  &[data-corner='3'] {
    bottom: -1px;
    right: -1px;
    border-top: none;
    border-left: none;
  }
  &[data-corner='4'] {
    bottom: -1px;
    left: -1px;
    border-top: none;
    border-right: none;
  }
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background: ${({ theme }) => fade(theme.colors.backgroundMajor, 0.9)};
`;

const MaskContent = styled.div`
  width: 244px;
  height: 244px;
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    font-size: 12px;
    line-height: 20px;
    text-align: center;
  }
  .scanedText {
    width: 210px;
    text-align: center;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Info = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 40px;
  span {
    font-size: 14px;
    line-height: 22px;
    color: ${({ theme }) => theme.colors.text60};
    margin-left: 10px;
  }
`;

function ScanLogin(props = {}) {
  const { onSuccess = _.noop, tabKey } = props;

  const dispatch = useDispatch();
  const { t } = useLang();

  const { status, initToken } = useSelector((state) => state[NAMESPACE]);

  const watchPolling = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/watchPolling`,
      payload: {
        effect: 'getStatus',
        interval: 5000,
      },
    });
  }, []);

  // 取消轮训
  const cancelPolling = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/getStatus@polling:cancel`,
    });
  }, []);

  // 开始轮训
  const startPolling = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/getStatus@polling`,
    });
  }, []);

  // 获取token
  const getToken = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/getToken`,
    }).then(() => {
      startPolling();
    });
  }, []);

  // 验证token
  const validationToken = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/validateToken`,
    }).then(() => {
      cancelPolling();
      const params = { finishUpgrade: true };
      window.localStorage.setItem('kucoinv2_login_key', JSON.stringify(SCANLOGINTABKEY));
      onSuccess(params);
    });
  }, []);

  // reset Token state
  const resetScanState = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/resetScan`,
    });
  }, []);

  // 刷新token
  const reGetToken = useCallback(() => {
    resetScanState();
    getToken();
  }, []);

  useEffect(() => {
    if (tabKey === SCANLOGINTABKEY) {
      switch (status) {
        case SCAN_AUTHORIZED:
          cancelPolling();
          validationToken();
          break;
        case SCAN_EXPIRED:
        case SCAN_CANCELED:
          cancelPolling();
          break;
        default:
          break;
      }
    }
    return () => {};
  }, [status, tabKey]);

  useEffect(() => {
    if (tabKey === SCANLOGINTABKEY) {
      watchPolling();
      getToken();
    }
    return () => {};
  }, [tabKey]);

  useEffect(() => {
    return () => {
      cancelPolling();
      resetScanState();
    };
  }, [tabKey]);

  const enCodeToken = encodeURIComponent(`kucoin:///authorizeToken?token=${initToken}`);

  return (
    <>
      <QrBox>
        <Corner data-corner="1" />
        <Corner data-corner="2" />
        <Corner data-corner="3" />
        <Corner data-corner="4" />
        {initToken === undefined ? (
          <Spin spinning />
        ) : (
          <Box style={{ padding: '16px', background: '#fff' }}>
            <QrCode value={`${window.location.origin}?link=${enCodeToken}`} size={164} />
            {/* <div className={classes.statusNotice}>Logging in...</div> */}
          </Box>
        )}
        {status === SCAN_EXPIRED || status === SCAN_CANCELED ? (
          <Mask>
            <MaskContent>
              <div>
                <p>{status === SCAN_EXPIRED ? t('qr.validate') : t('qr.canceled')}</p>
                <Button type="primary" size="large" onClick={reGetToken}>
                  {t('qr.refresh')}
                </Button>
              </div>
            </MaskContent>
          </Mask>
        ) : null}
        {status === SCAN_SCANNED || status === SCAN_AUTHORIZED ? (
          <Mask>
            <MaskContent>
              <div className="scanedText">
                {status === SCAN_SCANNED ? t('qr.scanned.text') : t('qr.authorized.text')}
              </div>
            </MaskContent>
          </Mask>
        ) : null}
      </QrBox>
      <Info>
        <img src={info} alt="" />
        <span>{t('scan.tip')}</span>
      </Info>
    </>
  );
}

export default ScanLogin;
